import React, { useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import koLocale from "@fullcalendar/core/locales/ko";
import type { EventClickArg } from "@fullcalendar/core";
import { Box } from "@chakra-ui/react";
import useSettlementManagementQuery from "@/features/my-order/features/settlement-management/hooks/useSettlementManagementQuery";
import useCalcSettlementCalendarDayWidth from "@/features/my-order/features/settlement-management/hooks/useCalcSettlementCalendarDayWidth";
import { format, startOfMonth, addMonths } from "date-fns";
import useGetUnoFeeList from "@/features/my-order/features/settlement-management/hooks/query/useGetUnoFeeList";
import {
  convertToFullCalendarEventType,
  createFullCalendarBillingDateEvent,
  getEventSortOrder,
} from "@/features/my-order/features/settlement-management/utils";
import { settlementCalendarSx } from "@/features/my-order/features/settlement-management/components/fee/calendarStyles";
import CalendarEventContent from "@/features/my-order/features/settlement-management/components/fee/CalendarEventContent";
import SettlementCalendarHeader from "@/features/my-order/features/settlement-management/components/fee/SettlementCalendarHeader";

function SettlementManagementCalendar() {
  const calendarRef = useRef<FullCalendar>(null);
  const { calendarWrapperRef, syncDayCellWidth } =
    useCalcSettlementCalendarDayWidth();
  const { query, setQuery } = useSettlementManagementQuery();
  const currentDate = query.date ? new Date(query.date) : new Date();

  const firstDayOfThisMonth = startOfMonth(currentDate);
  const formattedFirstDay = format(firstDayOfThisMonth, "yyyy-MM-dd");

  // 2. 다음 달 1일 구하기
  const firstDayOfNextMonth = startOfMonth(addMonths(currentDate, 1));
  const formattedNextMonthFirstDay = format(firstDayOfNextMonth, "yyyy-MM-dd");

  const params = {
    final_customs_clearance_date_gte: formattedFirstDay,
    final_customs_clearance_date_lt: formattedNextMonthFirstDay,
  };
  const { data } = useGetUnoFeeList(params);
  const refinedUnoFees =
    data?.unoFees.map((fee) => convertToFullCalendarEventType(fee)) ?? [];

  const updateDate = (date: Date) => {
    setQuery((prev) => ({
      ...prev,
      date,
      unoFeeId: undefined,
    }));
  };

  const handlePrev = () => {
    const api = calendarRef.current?.getApi();
    api?.prev();
    updateDate(api?.getDate() ?? new Date());
  };

  const handleNext = () => {
    const api = calendarRef.current?.getApi();
    api?.next();
    updateDate(api?.getDate() ?? new Date());
  };

  const handleToday = () => {
    const api = calendarRef.current?.getApi();
    api?.today();
    updateDate(api?.getDate() ?? new Date());
  };

  const handleEventClick = (info: EventClickArg) => {
    const unoFeeId = +info.event.id;
    const { isBillingDate } = info.event.extendedProps;

    if (isBillingDate) return;
    if (!unoFeeId) return;

    setQuery((prev) => ({
      ...prev,
      unoFeeId,
    }));
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  const selectedMonth =
    (calendarRef.current?.getApi()?.getDate()?.getMonth() ?? 0) + 1;
  const currentMonth = new Date().getMonth() + 1;

  return (
    <Box>
      <SettlementCalendarHeader
        year={year}
        month={month}
        isNextDisabled={currentMonth + 1 === selectedMonth}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
      />

      <Box
        boxShadow={"0 0 0 0.5px #D4D4D1"}
        border={"1px solid #D4D4D1"}
        ref={calendarWrapperRef}
        sx={settlementCalendarSx}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          initialDate={currentDate}
          locale={koLocale}
          headerToolbar={false}
          events={refinedUnoFees.concat(
            createFullCalendarBillingDateEvent(currentDate)
          )}
          dayMaxEvents={2}
          moreLinkText={(n) => `${n}개 더보기`}
          moreLinkClick="popover"
          eventContent={(eventInfo) => (
            <CalendarEventContent
              eventInfo={eventInfo}
              selectedUnoFeeId={query.unoFeeId}
            />
          )}
          dayCellContent={(arg) => arg.dayNumberText.replace("일", "")}
          fixedWeekCount={true}
          height="670px"
          windowResize={syncDayCellWidth}
          eventClick={handleEventClick}
          eventOrder={(a, b) => {
            const aOrder = getEventSortOrder(a);
            const bOrder = getEventSortOrder(b);

            return aOrder - bOrder;
          }}
          eventOrderStrict={true}
          dayPopoverFormat={{
            day: "numeric",
            weekday: "short",
          }}
        />
      </Box>
    </Box>
  );
}

export default SettlementManagementCalendar;
